import { useState } from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import { useWorkspace, Workspace } from '../contexts/WorkspaceContext';

export default function WorkspaceSwitcher() {
  const { workspaces, currentWorkspace, switchWorkspace, currentRole } = useWorkspace();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (workspaces.length > 1) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (workspace: Workspace) => {
    switchWorkspace(workspace.id);
    handleClose();
  };

  if (!currentWorkspace) {
    return null;
  }

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderRadius: 1,
          cursor: workspaces.length > 1 ? 'pointer' : 'default',
          '&:hover': workspaces.length > 1
            ? {
                bgcolor: 'action.hover',
              }
            : {},
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'primary.main',
            color: 'white',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {currentWorkspace.name.charAt(0).toUpperCase()}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {currentWorkspace.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {currentRole === 'OWNER' ? 'Owner' : 'Member'}
          </Typography>
        </Box>
        {workspaces.length > 1 && (
          <IconButton size="small" sx={{ opacity: 0.6 }}>
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            maxWidth: 300,
          },
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1, display: 'block' }}>
          Switch Workspace
        </Typography>
        <Divider />
        {workspaces.map((workspace) => (
          <MenuItem
            key={workspace.id}
            selected={workspace.id === currentWorkspace.id}
            onClick={() => handleSelect(workspace)}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'primary.main',
                  color: 'white',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {workspace.name.charAt(0).toUpperCase()}
              </Box>
            </ListItemIcon>
            <ListItemText
              primary={workspace.name}
              secondary={workspace.myRole === 'OWNER' ? 'Owner' : 'Member'}
              primaryTypographyProps={{ noWrap: true }}
              secondaryTypographyProps={{ noWrap: true }}
            />
            {workspace.id === currentWorkspace.id && (
              <CheckIcon fontSize="small" color="primary" />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

